import { Component, OnInit } from '@angular/core'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { filter, map, take, tap } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { TemplateTypes } from '../../email-template/email-template'
import { Member } from '../../member/member'
import { memberSelectors } from '../../member/member.selectors'
import { Participation } from '../../booking/participation/participation'
import { courseSelectors } from '../course.selectors'
import { Course } from '../course'
import { memberActions } from '../../member/member.actions'
import { bookingActions } from '../../booking/booking.actions'
import { Booking } from '../../booking/booking'
import { bookingSelectors } from '../../booking/booking.selectors'
import moment from 'moment-mini'

@Component({
  selector: 'course-communication-tab',
  templateUrl: './communication-tab.component.html',
  styleUrls: ['./communication-tab.component.scss']
})
export class CourseCommunicationTabComponent implements OnInit {
  private alreadyFetchedMemberIds: {
    memberId: number
    participationStatus: number
    bookingId: number
  }[] = []
  public bookings$: Observable<Booking[]>
  public contextBookingId$: Observable<number>
  public totalMembersInCourse$: BehaviorSubject<
    { memberId: number; participationStatus: number; bookingId: number }[]
  > = new BehaviorSubject<
    { memberId: number; participationStatus: number; bookingId: number }[]
  >([])
  public courseId$: Observable<number>
  public courseSelectors = courseSelectors
  public templateTypes = TemplateTypes
  public loadCourseParticipations: (
    course$: Observable<Course>,
    store: Store<AppState>
  ) => Observable<Participation[]>
  public loadCourseSelectableMembers: (
    course$: Observable<Course>,
    store: Store<AppState>
  ) => Observable<
    (Member & { participationStatus: number; bookingId: number })[]
  >
  constructor(private store: Store<AppState>) {}

  private getSelectableMemberId(participation: Participation): number {
    return participation.invitedMemberId || participation.memberId
  }

  ngOnInit() {
    this.courseId$ = this.store.select(courseSelectors.selectIdRouteParam).pipe(
      filter((id) => Boolean(id)),
      map((courseId) => +courseId)
    )

    this.courseId$.pipe(take(1)).subscribe((courseId) => {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { courseId },
            join: [
              { field: 'product' },
              { field: 'product.translations' },
              { field: 'linkedProduct' },
              { field: 'linkedProduct.translations' },
              { field: 'supervisorBookings' },
              { field: 'participations' }
            ]
          })
        })
      )
    })

    this.bookings$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.courseId$
    ]).pipe(
      map(([bookings, courseId]) => {
        return bookings
          .filter((b) => b.courseId === courseId)
          .sort((a, b) => (a?.startDate > b?.startDate ? -1 : 1))
      })
    )

    this.contextBookingId$ = this.bookings$.pipe(
      map((bookings) => {
        const now = moment()
        const upcomingBookings = bookings.filter((booking) =>
          moment(booking.startDate).isSameOrAfter(now)
        )

        return (upcomingBookings[upcomingBookings.length - 1] ?? bookings[0])
          ?.id
      })
    )

    this.loadCourseParticipations = (
      course$: Observable<Course>,
      store: Store<AppState>
    ) =>
      combineLatest([
        this.bookings$,
        course$,
        store.select(participationSelectors.selectAll)
      ]).pipe(
        map(([bookings, course, participations]) => {
          const uniqueParticipants: Participation[] = []

          for (const booking of bookings) {
            const bookingParticipations = participations.filter(
              (p) => p.bookingId === booking.id
            )

            for (const bookingParticipation of bookingParticipations) {
              const selectableMemberId =
                this.getSelectableMemberId(bookingParticipation)

              if (!selectableMemberId) {
                continue
              }

              if (
                !uniqueParticipants.find(
                  (participation) =>
                    this.getSelectableMemberId(participation) ===
                    selectableMemberId
                )
              ) {
                uniqueParticipants.push(bookingParticipation)
              }
            }
          }

          return uniqueParticipants
        }),
        tap((uniqueParticipants) => {
          const memberIdsToFetch = uniqueParticipants
            .map((participation) => ({
              memberId: this.getSelectableMemberId(participation),
              participationStatus: participation.status,
              bookingId: participation.bookingId
            }))
            .filter((item) => item.memberId)
            .filter(
              (item) =>
                !this.alreadyFetchedMemberIds.some(
                  (existing) => existing.memberId === item.memberId
                )
            )

          if (memberIdsToFetch.length) {
            this.alreadyFetchedMemberIds.push(...memberIdsToFetch)
            this.totalMembersInCourse$.next(this.alreadyFetchedMemberIds)
            this.store.dispatch(
              memberActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    id: {
                      [CondOperator.IN]: memberIdsToFetch.map(
                        (item) => item.memberId
                      )
                    }
                  }
                })
              })
            )
          }
        })
      )

    this.loadCourseSelectableMembers = (
      course$: Observable<Course>,
      store: Store<AppState>
    ) =>
      combineLatest([
        course$,
        this.totalMembersInCourse$,
        store.select(memberSelectors.selectEntities)
      ]).pipe(
        map(([course, totalMembersInCourse, memberEntities]) => {
          const members = totalMembersInCourse
            .map((item) => {
              const member = memberEntities[item.memberId]
              if (member) {
                return {
                  ...member,
                  participationStatus: item.participationStatus,
                  bookingId: item.bookingId
                }
              }
              return null
            })
            .filter(
              (
                member
              ): member is Member & {
                participationStatus: number
                bookingId: number
              } => !!member
            )
          return [...members]
        })
      )
  }
}
